import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchPsap,
  PSAPInfo,
} from '@rsos/reduxDataStore/src/features/capstone/psapInfo/psapInfoSlice';
import {
  fetchUser,
  fetchAllPermissions,
  fetchAppRoles,
  swallowThunkRejection,
} from '@rsos/sinatra';
import { AdminState } from '../../../reducers';
import { fetchSpatialLayers } from './spatialLayersSlice';

export type InitializationState = {
  initializing: boolean;
  initialized: boolean;
  initError?: string | null;
};

const initialState: InitializationState = {
  initializing: true,
  initialized: false,
  initError: null,
};

// Data loading only. Whether the user may be here is decided by the route's
// <RequireAccess> guard in app/Main.tsx; the catch below handles authentication
// (no session) rather than authorization.
export const initialize = createAsyncThunk<
  PSAPInfo,
  void,
  { state: AdminState }
>(
  'appDataSource/initialize',

  async (_, { dispatch, getState }) => {
    const isLoggedIn = getState().sinatra.user.isLoggedIn;
    try {
      await dispatch(fetchUser());
      // currentOrg/currentRole are resolved by fetchUser from the user's
      // capstone or central_station role, so they match the role the access
      // descriptors are evaluated against.
      const { currentOrg, currentRole } = getState().sinatra.user;

      if (currentOrg?.id && currentRole?.application) {
        // These thunks dispatch their own error actions on failure; catch the
        // rejections so these best-effort fetches cannot leak as unhandled rejections.
        dispatch(
          fetchAllPermissions(currentOrg.id, currentRole.application),
        ).catch(swallowThunkRejection);
        dispatch(fetchAppRoles(currentOrg.id, currentRole.application)).catch(
          swallowThunkRejection,
        );
      }
      dispatch(fetchSpatialLayers());
      if (!currentOrg?.name) {
        throw new Error('No admin organization for the current role');
      }
      // Awaited because the Map Layers and Discrepancy Reports routes are gated
      // on org capabilities, which come from this PSAP: opening the section's
      // gate first leaves those guards resolving an empty capability set, which
      // reads as "not entitled" and redirects out of admin.
      await dispatch(fetchPsap(`/psaps/${currentOrg.name}`)).unwrap();
      return getState().psaps.currentPSAP;
    } catch (err) {
      if (isLoggedIn) {
        throw err;
      }
      window.location.assign('/');
      throw new Error('Not logged in');
    }
  },
);

const appSlice = createSlice({
  name: 'appDataSource',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(initialize.pending, state => {
        state.initializing = true;
        state.initialized = false;
        state.initError = null;
      })
      .addCase(initialize.fulfilled, state => {
        state.initializing = false;
        state.initialized = true;
        state.initError = null;
      })
      .addCase(initialize.rejected, (state, { error = {} }) => {
        state.initializing = false;
        state.initialized = false;
        state.initError = error.message;
      });
  },
});

export const appReducer = appSlice.reducer;
export default appReducer;
